import React, { useEffect, useState } from 'react';
import api from '../../../services/api';
import { Table, Button, Modal, Form, Input, Select, Checkbox, InputNumber, Alert, Tag, Space, Image, Collapse } from 'antd';
import { formatCurrency, getDietaryTagColor } from '../../../utils/ui';
import ModernImageUpload from '../../UI/ModernImageUpload';
import MultipleImageUpload from '../../UI/MultipleImageUpload';
import AIDescriptionGenerator from '../../UI/AIDescriptionGenerator';
import { resolveImageSrc, getImageForCard } from '../../../utils/images';

function VendorMenus() {
	const [menus, setMenus] = useState([]);
	const [categories, setCategories] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');
	const [formOpen, setFormOpen] = useState(false);
	const [editing, setEditing] = useState(null);
	const [form] = Form.useForm();

	const load = async () => {
		try {
			// Get vendor info first to get vendor_id
			const vendorRes = await api.getMyVendor();
			const vendorId = vendorRes.data?.id;
			console.log('Vendor ID:', vendorId);
			
			const [categoriesRes, menusRes] = await Promise.all([
				api.getCategories().catch(() => ({ data: [] })),
				vendorId ? api.getMenus({ vendor_id: vendorId }).catch(() => ({ data: { data: [] } })) : Promise.resolve({ data: { data: [] } })
			]);
			console.log('Menus response:', menusRes);
			setCategories(categoriesRes.data || []);
			setMenus(menusRes.data.data || []);
		} catch (e) {
			console.error('Load error:', e);
			setError('Failed to load data');
			setCategories([]);
			setMenus([]);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => { load(); }, []);

	const openCreate = () => {
		setEditing(null);
		form.resetFields();
		form.setFieldsValue({
			name: '', description: '', price: 0, portions_per_person: 0.15, category_id: '', 
			images: [], is_vegetarian: true, is_vegan: false, is_gluten_free: false, is_spicy: false,
			spice_level: 0, preparation_time: 30,
		});
		setFormOpen(true);
	};

	const openEdit = (m) => {
		setEditing(m);
		form.resetFields();
		
		// Convert existing images to the format expected by MultipleImageUpload
		const existingImages = m.images ? m.images.map(img => ({
			url: img.url || img,
			thumb: img.thumb,
			medium: img.medium,
			large: img.large,
			id: img.id,
			preview: img.url || img.thumb || img
		})) : (m.image ? [{
			url: m.image,
			thumb: m.imageVariants?.thumb,
			medium: m.imageVariants?.medium,
			large: m.imageVariants?.large,
			preview: m.image
		}] : []);

		form.setFieldsValue({
			name: m.name || '', description: m.description || '', price: Number(m.price) || 0,
			portions_per_person: Number(m.portions_per_person) || 0.15, category_id: String(m.category_id || ''), 
			images: existingImages, is_vegetarian: !!m.is_vegetarian, is_vegan: !!m.is_vegan, 
			is_gluten_free: !!m.is_gluten_free, is_spicy: !!m.is_spicy,
			spice_level: m.spice_level || 0, preparation_time: m.preparation_time || 30,
		});
		setFormOpen(true);
	};

	const onSubmit = async () => {
		try {
			const values = await form.validateFields();
			console.log('Form values:', values);
			
			// Create FormData for file upload
			const formData = new FormData();
			
			// Add all form fields to FormData
			Object.keys(values).forEach(key => {
				if (key === 'images' && Array.isArray(values[key])) {
					console.log('Processing images array:', values[key]);
					// Add multiple images - only include files that have a .file property
					values[key].forEach((imageData, index) => {
						console.log(`Image ${index}:`, imageData);
						if (imageData && imageData.file) {
							console.log(`Adding image file ${index} to FormData:`, imageData.file);
							formData.append(`images[${index}]`, imageData.file);
						} else {
							console.log(`Skipping image ${index} - no file property:`, imageData);
						}
					});
				} else if (key === 'image' && values[key] instanceof File) {
					// Add single image (backward compatibility)
					formData.append('image', values[key]);
				} else if (key !== 'image' && key !== 'imageVariants' && key !== 'images') {
					// Handle boolean fields properly
					if (typeof values[key] === 'boolean') {
						formData.append(key, values[key] ? '1' : '0');
					} else {
						formData.append(key, values[key]);
					}
				}
			});
			
			console.log('FormData contents:');
			for (let [key, value] of formData.entries()) {
				console.log(key, value);
			}
			
			if (editing) {
				const result = await api.updateMenu(editing.id, formData);
				console.log('Update result:', result);
			} else {
				const result = await api.createMenu(formData);
				console.log('Create result:', result);
			}
			setFormOpen(false);
			await load();
		} catch (err) {
			console.error('Submit error:', err);
			if (err?.message) setError(err.message);
		}
	};

	const onDelete = async (id) => {
		if (!confirm('Delete this menu item?')) return;
		try {
			await api.deleteMenu(id);
			await load();
		} catch (err) {
			setError(err.message || 'Delete failed');
		}
	};

	if (loading) {
		return (
			<div className="min-h-[50vh] flex items-center justify-center">
				<div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-500"></div>
			</div>
		);
	}

	const columns = [
		{ 
			title: 'Image', 
			dataIndex: 'image', 
			key: 'image', 
			width: 80,
			render: (image, record) => {
				// Use Spatie MediaLibrary image variants
				const imageUrl = record.imageVariants?.medium || record.image;
				
				return (
					<Image
						src={imageUrl}
						alt={record.name}
						width={60}
						height={60}
						className="rounded object-cover"
						fallback="/images/placeholder.svg"
					/>
				);
			}
		},
		{ title: 'Name', dataIndex: 'name', key: 'name' },
		{ title: 'Category', dataIndex: ['category','name'], key: 'category' },
		{ title: 'Price', dataIndex: 'price', key: 'price', render: (p) => formatCurrency(p) },
		{ title: 'Portions/Person', dataIndex: 'portions_per_person', key: 'portions', render: (p) => p ? `${p}` : '0.15' },
		{ title: 'Prep (min)', dataIndex: 'preparation_time', key: 'prep' },
		{ title: 'Dietary', key: 'dietary', render: (_, m) => (
			<Space size={[4, 8]} wrap>
				{m.is_vegetarian && <Tag color={getDietaryTagColor('Vegetarian')}>Veg</Tag>}
				{m.is_vegan && <Tag color={getDietaryTagColor('Vegan')}>Vegan</Tag>}
				{m.is_gluten_free && <Tag color={getDietaryTagColor('Gluten-Free')}>Gluten Free</Tag>}
				{m.is_spicy && <Tag color="red">Spicy</Tag>}
			</Space>
		)},
		{ title: '', key: 'actions', render: (_, m) => (
			<div className="flex gap-2">
				<Button onClick={() => openEdit(m)}>Edit</Button>
				<Button danger onClick={() => onDelete(m.id)}>Delete</Button>
			</div>
		)},
	];

	return (
		<div className="max-w-7xl mx-auto p-6">
			<div className="flex items-center justify-between mb-4">
				<h1 className="text-2xl font-bold">Manage Menus</h1>
				<Button type="primary" onClick={openCreate}>Add Menu</Button>
			</div>
			{error && (<Alert type="error" message={error} className="mb-4" />)}

			<Table columns={columns} dataSource={menus} rowKey="id" pagination={{ pageSize: 10 }} scroll={{ x: true }} />

			<Modal
				title={editing ? 'Edit Menu' : 'Add Menu'}
				open={formOpen}
				onCancel={() => setFormOpen(false)}
				onOk={onSubmit}
				okText="Save"
				destroyOnHidden
			>
				<Form form={form} layout="vertical">
					<Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter name' }]}>
						<Input />
					</Form.Item>
					<Form.Item name="description" label="Description">
						<Input.TextArea rows={3} />
					</Form.Item>
					
					<Collapse 
						items={[{
							key: 'ai-description',
							label: '🤖 AI Description Generator',
							children: (
								<AIDescriptionGenerator
									menuItem={form.getFieldsValue()}
									onDescriptionGenerated={(description) => {
										form.setFieldsValue({ description });
									}}
									placeholder="Enter additional details about your dish (cooking method, special ingredients, unique flavors, etc.) to generate a compelling description..."
								/>
							)
						}]}
						className="mb-4"
					/>
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
						<Form.Item name="price" label="Price (₹)" rules={[{ required: true, message: 'Please enter price' }]}>
							<InputNumber style={{ width: '100%' }} min={0} step={0.01} />
						</Form.Item>
						<Form.Item name="portions_per_person" label="Portions per Person" rules={[{ required: true, message: 'Please enter portions per person' }]}>
							<InputNumber 
								style={{ width: '100%' }} 
								min={0.01} 
								max={1.0} 
								step={0.01} 
								placeholder="0.15"
								tooltip="How many portions of this dish one person typically needs (0.15 = 1 portion serves ~6-7 people)"
							/>
						</Form.Item>
					</div>
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
						<Form.Item name="category_id" label="Category" rules={[{ required: true, message: 'Please select category' }]}>
							<Select options={(categories || []).map(c => ({ label: c.name, value: String(c.id) }))} />
						</Form.Item>
					</div>
					
					<Form.Item name="images" label="Menu Images">
						<MultipleImageUpload
							maxFiles={10}
						/>
					</Form.Item>
					<div className="grid grid-cols-2 gap-4">
						<Form.Item name="is_vegetarian" valuePropName="checked">
							<Checkbox>Vegetarian</Checkbox>
						</Form.Item>
						<Form.Item name="is_vegan" valuePropName="checked">
							<Checkbox>Vegan</Checkbox>
						</Form.Item>
						<Form.Item name="is_gluten_free" valuePropName="checked">
							<Checkbox>Gluten Free</Checkbox>
						</Form.Item>
						<Form.Item name="is_spicy" valuePropName="checked">
							<Checkbox>Spicy</Checkbox>
						</Form.Item>
					</div>
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
						<Form.Item name="spice_level" label="Spice Level (0-5)">
							<InputNumber min={0} max={5} style={{ width: '100%' }} />
						</Form.Item>
						<Form.Item name="preparation_time" label="Preparation Time (min)">
							<InputNumber min={1} style={{ width: '100%' }} />
						</Form.Item>
					</div>
				</Form>
			</Modal>
		</div>
	);
}

export default VendorMenus;
